import {BehaviorSubject} from "rxjs";
import {useSubjectSnapshot} from "features/core/subject.hooks";
import {bettingTypes} from "features/core/definitions";
import {postCoupon, toSubmitError} from "features/betslip/lib/submit";

const LONGTERM_BETTING_TYPE_ID = 1889;

export const longtermBettingType = bettingTypes.find((bt: any) => bt.id === LONGTERM_BETTING_TYPE_ID);

interface LongtermSlipItem {
    hippodrome: any;
    horse: any;
}

interface LongtermSlipOptions {
    multiply: number;
    usePointBalance: boolean;
    shareCoupon: boolean;
    acceptChangedOdds: boolean;
    errorMessage: string | null;
    inProgress: boolean;
}

const defaultLongtermSlipOptions: LongtermSlipOptions = {
    multiply: 20,
    usePointBalance: false,
    shareCoupon: false,
    acceptChangedOdds: true,
    errorMessage: null,
    inProgress: false,
};

const longtermBetslipSubject = new BehaviorSubject<LongtermSlipItem | null>(null);
const longtermSlipOptionsSubject = new BehaviorSubject<LongtermSlipOptions>(defaultLongtermSlipOptions);

class LongtermBetslipService {
    private item: LongtermSlipItem | null = null;
    private options: LongtermSlipOptions = {...defaultLongtermSlipOptions};

    getSelection(): LongtermSlipItem | null {
        return this.item;
    }

    getOptions(): LongtermSlipOptions {
        return this.options;
    }

    isAdded(hippodrome: any, horse: any): boolean {
        return !!this.item &&
            this.item.hippodrome._id === hippodrome._id &&
            String(this.item.horse.no) === String(horse.no);
    }

    toggle(hippodrome: any, horse: any) {
        if (hippodrome.status !== 1) return;
        if (horse.status !== "o") return;

        if (this.isAdded(hippodrome, horse)) {
            this.item = null;
        } else {
            this.item = {hippodrome, horse};
        }
        this.update();
    }

    clear() {
        this.item = null;
        this.update();
        this.setOptions({...defaultLongtermSlipOptions});
    }

    setOptions(patch: Partial<LongtermSlipOptions>) {
        this.options = {...this.options, ...patch};
        this.updateOptions();
    }

    getTotalAmount(): number {
        return Number(this.options.multiply) || 0;
    }

    getMaxWin(): number {
        if (!this.item) return 0;
        return Number(this.item.horse.win_odds) * Number(this.options.multiply);
    }

    // `imgData`: kuponun ekran görüntüsü (data URL). Müşterekteki desenin aynısı - çağıran
    // html2canvas ile üretip veriyor, üretemezse boş string geliyor ve alan yine de gönderiliyor.
    async submit(save: boolean, imgData: any = ""): Promise<any> {
        if (this.options.inProgress || !this.item) {
            return null;
        }

        this.setOptions({inProgress: true});

        try {
            const couponData = this.buildCouponData();
            return await postCoupon({
                coupon: couponData,
                save,
                usePointBalance: this.options.usePointBalance,
                shareCoupon: this.options.shareCoupon,
                couponImage: imgData,
            });
        } catch (error: any) {
            return toSubmitError(error);
        } finally {
            this.setOptions({inProgress: false});
        }
    }

    private buildCouponData() {
        if (!this.item) {
            throw new Error("Seçili bahis bulunamadı.");
        }

        const {hippodrome, horse} = this.item;
        if (!horse.win_odds || Number(horse.win_odds) === 1) {
            throw new Error("Seçilen bahis için güncel oran bulunamadı. Lütfen sepetinizdeki seçimi kaldırıp tekrar deneyin.");
        }

        const reversedDate = `${hippodrome._date.split('-')[2]}${hippodrome._date.split('-')[1]}${hippodrome._date.split('-')[0]}`;
        const cardKey = `${reversedDate}-${hippodrome.hippodrome_key.toLowerCase()}`;
        const legKey = `${cardKey}_${LONGTERM_BETTING_TYPE_ID}`;
        const price = Number(this.options.multiply);

        return {
            betCount: 1,
            betType: LONGTERM_BETTING_TYPE_ID,
            cardId: hippodrome._id,
            card: cardKey,
            multiplier: price,
            system: false,
            price: price.toFixed(2),
            legs: [{[legKey]: {...horse, hippodrome: hippodrome.hippodrome}}],
            poolUnit: Math.round(Number(longtermBettingType?.price ?? 1) * 100),
            systemAmount: 5,
            fob: true,
            oddRatio: horse.win_odds.toString(),
            oddId: horse.no.toString(),
        };
    }

    private update() {
        longtermBetslipSubject.next(this.item);
    }

    private updateOptions() {
        longtermSlipOptionsSubject.next(this.options);
    }
}

export const LongtermBetslip = new LongtermBetslipService();

export function useLongtermBetslip(): LongtermSlipItem | null {
    return useSubjectSnapshot(longtermBetslipSubject, () => LongtermBetslip.getSelection());
}

export function useLongtermSlipOptions(): LongtermSlipOptions {
    return useSubjectSnapshot(longtermSlipOptionsSubject, () => LongtermBetslip.getOptions());
}
